import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LogOut, User, Sparkles, ChevronDown, MessageSquare, CreditCard, Crown,
  Settings, LayoutDashboard, Receipt, RefreshCw, Calendar, Shield,
} from 'lucide-react';
import { useAuthContext } from '../contexts/AuthContext';
import { useSalePage } from '../contexts/SalePageContext';
import { signOut } from 'firebase/auth';
import { auth, db } from '../lib/firebase';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import AuthModal from './AuthModal';

const FONT = '"IBM Plex Sans Thai", system-ui, sans-serif';
const HEAD = "'Anuphan', sans-serif";
const INK = '#0E3A40';

function planLabel(plan?: string) {
  if (!plan) return 'สมาชิก';
  if (plan === 'free') return 'ทดสอบระบบ';
  if (plan === 'basic') return 'Basic';
  if (plan === 'premium') return 'Premium';
  if (plan === 'premium_vip') return 'Premium VIP';
  return plan.toUpperCase();
}

/**
 * Shared SaleDi header for every saledi.web.app page. Rebuilt to match the Claude Design
 * system (sticky glass bar, Anuphan/IBM Plex Sans Thai, teal→blue brand, feature dropdown +
 * account menu). Preserves the original behaviour: profile-image load, pending-request check,
 * AuthModal for logged-out signup, admin entry, and logout.
 */
const SalePageNavbar: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { subscription } = useSalePage();
  const [featOpen, setFeatOpen] = useState(false);
  const [acctOpen, setAcctOpen] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [, setHasPendingRequest] = useState(false);
  const [profileImage, setProfileImage] = useState<string>('');
  const featRef = useRef<HTMLDivElement>(null);
  const acctRef = useRef<HTMLDivElement>(null);

  const dashboardPath = typeof window !== 'undefined' && window.location.hostname !== 'saledi.web.app'
    ? '/salepage/dashboard' : '/dashboard';

  useEffect(() => {
    const loadProfileImage = async () => {
      if (!user?.uid) return;
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) setProfileImage(userDoc.data().profileImage || '');
      } catch (error) {
        console.error('Error loading profile image:', error);
      }
    };
    loadProfileImage();
  }, [user?.uid]);

  useEffect(() => {
    const checkPendingRequest = async () => {
      if (!user?.uid || subscription) { setHasPendingRequest(false); return; }
      try {
        const q = query(
          collection(db, 'salePagePaymentNotifications'),
          where('userId', '==', user.uid),
          where('status', '==', 'pending')
        );
        const snap = await getDocs(q);
        setHasPendingRequest(!snap.empty);
      } catch (error) {
        console.error('Error checking pending request:', error);
        setHasPendingRequest(false);
      }
    };
    checkPendingRequest();
  }, [user?.uid, subscription]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (featRef.current && !featRef.current.contains(t)) setFeatOpen(false);
      if (acctRef.current && !acctRef.current.contains(t)) setAcctOpen(false);
    };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, []);

  const handleLogout = async () => {
    try {
      localStorage.removeItem('auth_user_cache');
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    }
    navigate('/');
  };

  const go = (path: string) => { setFeatOpen(false); setAcctOpen(false); navigate(path); };

  // Messages & Payments require an active subscription (SalePageGuard requireActive) — hide them
  // when the plan can't use them, so the menu never bounces the user back to the dashboard.
  const hasValidSub = !!subscription && (subscription.status === 'active' || subscription.status === 'trial');
  const isAdmin = user?.email === 'viitamint@gmail.com' || (user as any)?.role === 'admin';
  const acctName: string =
    (user as any)?.displayName || (user as any)?.name || (user as any)?.username ||
    (user?.email || '').split('@')[0] || 'บัญชีของฉัน';

  const menuLink: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 10, padding: '11px 12px',
    borderRadius: 11, fontSize: 14, color: INK, cursor: 'pointer',
  };

  const featureItems: { to: string; label: string; icon: JSX.Element; show?: boolean }[] = [
    { to: '/salepage/messages', label: 'ข้อความ', icon: <MessageSquare style={{ width: 16, height: 16, color: '#3A93B9' }} />, show: hasValidSub },
    { to: '/salepage/payments', label: 'การชำระเงิน', icon: <CreditCard style={{ width: 16, height: 16, color: '#00B4A1' }} />, show: hasValidSub },
    { to: '/salepage/payment-history', label: 'ประวัติการชำระ', icon: <Receipt style={{ width: 16, height: 16, color: '#5B6FCB' }} /> },
    { to: '/salepage/settings', label: 'ตั้งค่า', icon: <Settings style={{ width: 16, height: 16, color: '#5B7D80' }} /> },
    { to: '/salepage/renew', label: 'ต่ออายุ / แพ็คเกจ', icon: <RefreshCw style={{ width: 16, height: 16, color: '#FF7A45' }} /> },
    { to: '/salepage/booking-addon-checkout', label: 'เพิ่มระบบจองคิว', icon: <Calendar style={{ width: 16, height: 16, color: '#5B6FCB' }} />, show: subscription?.plan === 'premium' || subscription?.plan === 'premium_vip' },
  ];

  return (
    <>
      <header style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(243,248,248,0.9)', backdropFilter: 'blur(18px)', borderBottom: '1px solid rgba(14,58,64,0.08)', fontFamily: FONT }}>
        <div style={{ maxWidth: 1240, margin: '0 auto', padding: '12px clamp(16px,3vw,32px)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {/* Logo */}
          <div onClick={() => navigate(user ? dashboardPath : '/')} style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer' }}>
            <span style={{ width: 32, height: 32, borderRadius: 10, background: 'linear-gradient(135deg, #00B4A1, #3A93B9)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontFamily: HEAD, fontWeight: 700, fontSize: 16, boxShadow: '0 8px 18px -8px rgba(0,180,161,0.55)' }}>S</span>
            <span style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 19, letterSpacing: '-0.01em', color: INK }}>SaleDi</span>
          </div>

          {user && (
            <nav style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 2, background: 'oklch(0.96 0.016 175)', border: '1px solid oklch(0.915 0.022 175)', borderRadius: 999, padding: 4, position: 'relative' }} ref={featRef}>
              <button type="button" onClick={() => navigate(dashboardPath)} style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: FONT, fontSize: 14, color: '#3C6367', padding: '8px 15px', borderRadius: 999, border: 0, background: 'transparent', cursor: 'pointer' }}>
                <LayoutDashboard style={{ width: 15, height: 15 }} /> Dashboard
              </button>
              <button type="button" onClick={() => setFeatOpen((v) => !v)} style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: FONT, fontSize: 14, color: '#3C6367', padding: '8px 15px', borderRadius: 999, border: 0, background: 'transparent', cursor: 'pointer' }}>
                <Sparkles style={{ width: 15, height: 15, color: '#5B6FCB' }} /> ฟีเจอร์ของฉัน <ChevronDown style={{ width: 13, height: 13 }} />
              </button>
              {featOpen && (
                <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, minWidth: 210, background: '#fff', border: '1px solid rgba(14,58,64,0.1)', borderRadius: 16, boxShadow: '0 26px 50px -22px rgba(14,58,64,0.4)', padding: 8, zIndex: 80 }}>
                  {featureItems.filter((f) => f.show !== false).map((f) => (
                    <div key={f.to} style={menuLink} onClick={() => go(f.to)}>{f.icon} {f.label}</div>
                  ))}
                </div>
              )}
            </nav>
          )}

          {/* Right side */}
          {user ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, position: 'relative', marginLeft: user ? 0 : 'auto' }} ref={acctRef}>
              {subscription && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#0E3A40', color: '#fff', fontSize: 12.5, fontWeight: 500, padding: '6px 13px', borderRadius: 999 }}>
                  <Crown style={{ width: 13, height: 13, color: '#FF7A45' }} /> {planLabel(subscription?.plan)}
                </span>
              )}
              <button type="button" onClick={() => setAcctOpen((v) => !v)} style={{ display: 'flex', alignItems: 'center', gap: 8, border: 0, background: 'transparent', cursor: 'pointer', padding: 0 }}>
                <span style={{ width: 38, height: 38, borderRadius: 999, overflow: 'hidden', background: '#EAF1F1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {profileImage ? (
                    <img src={profileImage} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                  ) : (
                    <User style={{ width: 18, height: 18, color: '#3A93B9' }} />
                  )}
                </span>
                <ChevronDown style={{ width: 15, height: 15, color: '#5B7D80' }} />
              </button>
              {acctOpen && (
                <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, minWidth: 230, background: '#fff', border: '1px solid rgba(14,58,64,0.1)', borderRadius: 16, boxShadow: '0 26px 50px -22px rgba(14,58,64,0.4)', padding: 8, zIndex: 80 }}>
                  <div style={{ padding: '10px 12px', borderBottom: '1px solid #EEF4F4', marginBottom: 6 }}>
                    <div style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 14, color: INK, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{acctName}</div>
                    <div style={{ fontSize: 12, color: '#5B7D80', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email || ''}</div>
                  </div>
                  <div style={menuLink} onClick={() => go(dashboardPath)}><LayoutDashboard style={{ width: 16, height: 16 }} /> แดชบอร์ดของฉัน</div>
                  <div style={menuLink} onClick={() => go('/salepage/settings')}><Settings style={{ width: 16, height: 16 }} /> ตั้งค่า</div>
                  <div style={menuLink} onClick={() => go('/salepage/renew')}><RefreshCw style={{ width: 16, height: 16 }} /> จัดการแพ็คเกจ</div>
                  {isAdmin && (
                    <div style={menuLink} onClick={() => go('/admin')}><Shield style={{ width: 16, height: 16, color: '#5B6FCB' }} /> Admin</div>
                  )}
                  <div style={{ ...menuLink, color: '#C0392B' }} onClick={() => { setAcctOpen(false); handleLogout(); }}><LogOut style={{ width: 16, height: 16 }} /> ออกจากระบบ</div>
                </div>
              )}
            </div>
          ) : (
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
              <button type="button" onClick={() => navigate('/login?redirect=/dashboard')} style={{ padding: '9px 16px', color: INK, fontFamily: FONT, fontSize: 14, fontWeight: 500, background: 'transparent', border: 0, cursor: 'pointer' }}>
                เข้าสู่ระบบ
              </button>
              <button type="button" onClick={() => setShowRegisterModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', background: 'linear-gradient(120deg, #00B4A1, #3A93B9)', color: '#fff', borderRadius: 999, fontFamily: HEAD, fontSize: 14, fontWeight: 600, border: 0, cursor: 'pointer', boxShadow: '0 12px 26px -14px rgba(0,180,161,0.6)' }}>
                สมัครใช้งาน
              </button>
            </div>
          )}
        </div>
      </header>

      <AuthModal
        isOpen={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        mode="register"
        themeColor="#00B4A1"
        signupSource="salepage"
        plan="free"
        onLoginSuccess={() => { setShowRegisterModal(false); window.location.reload(); }}
      />
    </>
  );
};

export default SalePageNavbar;
